import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Card, Button, Spinner, Alert } from "react-bootstrap";
import { authFetch } from "../utils/authFetch";
import { toast } from "react-toastify";
import { BonProductieForm } from "./Form";
import ViewNotaModal from "../ViewNotaModal";
import CostAllocation from '../nc/CostAllocation';
import { useAuth } from '../auth/AuthContext';

const comparable = d => d ? JSON.stringify({
  bxdoc_no: d.bxdoc_no || null,
  bxdoc_date: new Date(d.bxdoc_date).toISOString(),
  bxgestiune_id: d.bxgestiune_id || null, bxgestiune2_id: d.bxgestiune2_id || null,
  produs_finit_id: d.produs_finit_id || null, cantitate_produsa: Number(d.cantitate_produsa),
  contact_id: d.contact_id || null, observatii: d.observatii || '',
  linii: (d.linii_lns || d.linii || []).map(l => ({ id: l.id ?? null, bxpart_id: l.bxpart_id, qty: Number(l.qty) })),
}) : '';

export default function BonProductieEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showNota, setShowNota] = useState(false);
  const [draft, setDraft] = useState(null);
  const { user } = useAuth();
  const isFinal = !!(doc?.is_closed || doc?.bxfinal_bool);
  const dirty = comparable(doc) !== comparable(draft);
  const canFinalize = user?.is_superuser || user?.perms?.some(p => ['STOC_WRITE', 'NC_WRITE'].includes(p));

  const fetchDoc = async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await authFetch(`/bon-productie/${id}`);
      setDoc(data);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDoc();
  }, [id]);

  const handleSubmit = async (payload) => {
    setBusy(true);
    try {
      await authFetch(`/bon-productie/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      toast.success("Bonul de producție a fost actualizat cu succes!");
      fetchDoc();
    } catch (e) {
      toast.error(`Eroare la actualizare: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const changeFinalization = async () => {
    setBusy(true); setErr('');
    try {
      await authFetch(`/bon-productie/${id}/${isFinal ? 'unfinalize' : 'finalize'}`, { method: 'POST' });
      await fetchDoc();
      toast.success(isFinal ? 'Bonul a fost redeschis.' : 'Bonul a fost finalizat și repartizarea verificată.');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <Container className="py-3">
      {err && <Alert variant="danger">{err}</Alert>}

      {loading ? (
        <div className="py-5 text-center">
          <Spinner animation="border" />
        </div>
      ) : (
        <Card className="shadow-sm">
          <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
            <h4 className="mb-0">Editează Bon de Producție: {doc?.bxdoc_no || `#${doc?.id}`}</h4>
            <div className="d-flex gap-2">
              {canFinalize && <Button variant="warning" size="sm" disabled={busy || (!isFinal && dirty)}
                title={dirty ? 'Salvează modificările înainte de finalizare.' : ''} onClick={changeFinalization}>
                {isFinal ? 'Redeschide' : 'Finalizează'}
              </Button>}
              <Button variant="info" size="sm" onClick={() => setShowNota(true)}>
                📄 Vezi Notă Contabilă
              </Button>
              <Button variant="light" size="sm" onClick={() => navigate("/bon-productie")}>
                Înapoi la Listă
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            <BonProductieForm
              initialDoc={doc}
              onSubmit={handleSubmit}
              busy={busy}
              isClosed={isFinal}
              onChangePayload={setDraft}
            />
            <CostAllocation recType={6} docId={id} refreshKey={doc} />
          </Card.Body>
        </Card>
      )}

      {doc && (
        <ViewNotaModal
          recType={6}
          docId={Number(doc.id)}
          show={showNota}
          onHide={() => setShowNota(false)}
        />
      )}
    </Container>
  );
}
